import React from 'react';
import { Link } from 'react-router-dom';
import PageHero from '../components/PageHero';
import FinalCTA from '../components/FinalCTA';
import Seo from '../components/Seo';
import LotusIcon from '../components/LotusIcon';
import { Check } from 'lucide-react';
import { IMAGES } from '../mock';

const mission = [
  'Feel seen and heard',
  'Gain clarity and direction',
  'Develop tools that actually work',
  'Build confidence and emotional resilience',
  'Reconnect with your sense of self',
];

export default function About() {
  return (
    <>
      <Seo
        title="About Chesapeake Counseling Center"
        description="Learn about Chesapeake Counseling Center's heart for healing. Trauma, EMDR, and anxiety therapy in Chesapeake, VA with a compassionate, evidence-based team."
        path="/about"
      />
      <PageHero
        eyebrow="About Us"
        title="Rediscover"
        highlight="the Light Within"
        subtitle="Everyone carries a light within them. Sometimes life’s pain, trauma, loss, or anxiety can make it difficult to see. Our mission is to help you reconnect with the light that has always been there."
        crumbs={[{ label: 'Home', to: '/' }, { label: 'About' }]}
      />

      <section className="section-pad" style={{ background: 'var(--warm-white)' }}>
        <div className="max-w-6xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-12 items-start">
          <div className="space-y-5 text-[color:var(--deep-sage)] leading-relaxed reveal">
            <p>We specialize in helping individuals and couples navigate trauma, anxiety, and relationship challenges with compassion and clarity. Our goal is not just to help you cope—but to help you truly heal and reconnect with yourself.</p>
            <p className="text-[color:var(--taupe-gray)]">Many of our clients come to us feeling overwhelmed, stuck, or exhausted from carrying too much for too long. On the outside, life may look “fine,” but internally it feels heavy, confusing, or disconnected.</p>
            <p className="text-[color:var(--taupe-gray)]">Our approach is grounded, supportive, and tailored to you. We use evidence-based practices such as EMDR, cognitive and somatic interventions, and nervous system regulation to help you move forward in a way that feels safe and sustainable.</p>
            <p className="italic">Therapy is not about fixing you — it’s about helping you understand yourself, release what no longer serves you, and step into a more empowered version of your life.</p>
          </div>

          <div className="reveal card-soft overflow-hidden">
            <img src={IMAGES.officeCozy} alt="Peaceful therapy office" className="w-full h-80 object-cover" />
            <div className="p-6 md:p-8 relative">
              <div className="absolute right-4 bottom-2 opacity-70"><LotusIcon className="w-16 h-16" /></div>
              <div className="eyebrow mb-2">Our Mission</div>
              <h3 className="font-serif-display text-2xl text-[color:var(--deep-sage)] mb-4">We create a space where you can</h3>
              <ul className="space-y-2.5">
                {mission.map(m => (
                  <li key={m} className="flex items-start gap-2 text-sm text-[color:var(--deep-sage)]">
                    <Check className="w-4 h-4 mt-0.5 text-[color:var(--dusty-rose)]" /> {m}
                  </li>
                ))}
              </ul>
              <div className="mt-5 font-script text-2xl text-[color:var(--dusty-rose)]">Healing is possible — and you don’t have to do it alone.</div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-pad" style={{ background: 'var(--cream)' }}>
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <div className="eyebrow mb-3">Explore</div>
          <h2 className="section-title mb-8">Where would you like to start?</h2>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/services" className="btn-primary">Our Services</Link>
            <Link to="/team" className="btn-secondary">Meet the Team</Link>
            <Link to="/why-ccc" className="btn-secondary">Why CCC</Link>
          </div>
        </div>
      </section>

      <FinalCTA />
    </>
  );
}
